import { motion } from 'framer-motion';
import { Navbar } from '../../components/layout/Navbar';
import { Footer } from '../../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users, Clock, CheckCircle, Target, Lightbulb } from 'lucide-react';

export default function RetailExpoPage() {
  const highlights = [
    '100+ Exhibitors from Finance & Investment sector',
    '10,000+ Expected retail investors',
    'Expert-led workshops and seminars',
    'One-on-one investor meetings',
    'Networking opportunities with industry leaders',
    'Live trading demonstrations'
  ];

  const exhibitorCategories = [
    'Stock Broking Firms',
    'Mutual Fund Houses',
    'Insurance Companies',
    'Fintech Startups',
    'Wealth Management',
    'Banking Services'
  ];

  return (
    <div className="page-transition" data-testid="retail-expo-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="India Retail Investors Expo"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 hero-overlay" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                <span className="text-gold text-sm font-medium uppercase tracking-wider">
                  Flagship Event
                </span>
              </span>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-7xl text-white mb-6">
                India Retail <span className="text-gold">Investors Expo</span>
              </h1>
              
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl">
                India's premier platform connecting retail investors with leading financial institutions, investment opportunities, and wealth management solutions.
              </p>

              <div className="flex flex-wrap gap-6 text-white/90 mb-10">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  <span>February 15-16, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gold" />
                  <span>HITEX Exhibition Center, Hyderabad</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  <span>10,000+ Attendees</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                  Register Interest
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors">
                  Become an Exhibitor
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About the Expo */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                  About the Event
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                  Empowering Retail Investors
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  The India Retail Investors Expo brings together the best minds in finance and investment under one roof. This two-day event offers unparalleled opportunities for retail investors to learn, network, and discover new investment avenues.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  Whether you're a first-time investor or a seasoned trader, the expo provides valuable insights, expert guidance, and direct access to top financial service providers.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {highlights.map((highlight) => (
                    <div key={highlight} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-navy text-sm">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                <img
                  src="https://images.unsplash.com/photo-1740727263760-d220d5a90d9f?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Expo floor"
                  className="w-full h-64 object-cover"
                />
                <img
                  src="https://images.pexels.com/photos/7567438/pexels-photo-7567438.jpeg"
                  alt="Seminar session"
                  className="w-full h-64 object-cover mt-8"
                />
                <img
                  src="https://images.unsplash.com/photo-1727931287903-b24dd8011a56?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Networking"
                  className="w-full h-64 object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1560439514-07abbb294a86?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Exhibition booth"
                  className="w-full h-64 object-cover mt-8"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Exhibitor Categories */}
        <section className="py-20 lg:py-32 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Exhibitor Categories
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                Who Exhibits at IRIE?
              </h2>
              <p className="text-muted-foreground text-lg">
                The expo features a diverse range of exhibitors from across the financial services industry.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {exhibitorCategories.map((category, index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 hover-lift text-center"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="font-heading text-xl text-navy">{category}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Be Part of India's Biggest <span className="text-gold">Investment Event</span>
              </h2>
              <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
                Whether you're looking to exhibit, sponsor, or attend, contact us today to learn more about participation opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                  Become an Exhibitor
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors">
                  Sponsorship Opportunities
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
